import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/*
 * Global game state (lightweight).
 * All UI components subscribe to slices instead of prop-drilling or Redux.
 * We persist only non-sensitive, small values to localStorage so they survive refreshes.
 */

const useGameState = create(
  persist(
    (set) => ({
      // ────────────────────────────────────────────────
      // Canonical state keys – keep in sync with docs
      // ────────────────────────────────────────────────
      quotaLeft: 5, // daily free interactions remaining
      potTotal: 0, // total USDC in the monthly prize pot
      boroughs: {}, // { boroughName: { risk: number, potShare: number } }
      streak: 0, // current user streak multiplier level

      // Player identity -------------------------------------------------
      selectedCharacter: 'vinnie', // default starting shark
      introCompleted: false, // onboarding dialogue shown

      // Chat limits / monetisation -------------------------------
      dailyQuota: 0,
      cooldownEndsAt: null, // ISO string or null

      core: {
        status: 'idle',
        playerId: null,
        cash: 0,
        debt: 0,
        heat: 0,
        districtId: null,
        reputationPoints: 0,
        inventoryCapacity: 0,
        inventory: [],
        version: 0,
        day: null,
        turnsRemaining: null,
        updatedAt: null,
      },

      // Player wallet / market info -----------------------------------
      playerCash: 0, // USD balance visible to player
      movesLeft: 5, // alias of quotaLeft for clarity; kept separate to avoid breaking tests
      reputation: 0.5, // Player reputation score
      level: 1, // Player level
      xp: 0, // Player experience points
      marketTicker: [], // array of { symbol: string, delta: number }

      leaderboard: [], // [{ rank, name, scoreDelta }]
      // Track player current borough selection (null until user picks)
      currentBorough: null,
      setCurrentBorough: (b) => set({ currentBorough: b }),

      // ────────────────────────────────────────────────
      // ENHANCED ECONOMIC SIMULATION FEATURES
      // ────────────────────────────────────────────────
      
      // Borough economy simulation
      boroughEconomy: {}, // { boroughName: { supply: number, demand: number, risk: number, reward: number } }
      
      // Monthly pot system integration
      monthlyPot: {
        total: 0,
        playerContribution: 0,
        distributionDate: null,
        lastContribution: null,
        potHistory: [] // Array of monthly pot snapshots
      },
      
      // Economic transactions
      transactions: [], // Array of player economic actions
      
      // Agent performance tracking
      agentPerformance: {
        vinnie: { interactions: 0, successRate: 0, lastInteraction: null },
        tommy: { interactions: 0, successRate: 0, lastInteraction: null },
        carmela: { interactions: 0, successRate: 0, lastInteraction: null },
        bigboss: { interactions: 0, successRate: 0, lastInteraction: null }
      },

      // ────────────────────────────────────────────────
      // Mutators – always use functional set to preserve
      // atomic updates when multiple slices dispatch.
      // ────────────────────────────────────────────────
      setQuotaLeft: (q) => set({ quotaLeft: q }),
      setPotTotal: (v) => set({ potTotal: v }),
      setBoroughs: (b) => set({ boroughs: b }),
      setStreak: (s) => set({ streak: s }),
      setSelectedCharacter: (character) => set({ selectedCharacter: character || 'vinnie' }),
      setIntroCompleted: (flag = true) => set({ introCompleted: !!flag }),
      applyCoreState: (core) =>
        set((state) => ({
          core: { ...state.core, ...core },
          playerCash: core.cash ?? state.playerCash,
          currentBorough: core.districtId ?? state.currentBorough,
          reputation: core.reputationPoints ?? state.reputation,
          movesLeft: core.turnsRemaining ?? state.movesLeft,
          quotaLeft: core.turnsRemaining ?? state.quotaLeft,
        })),

      setCash: (v) => set({ playerCash: v }),
      setMovesLeft: (m) => set({ movesLeft: m, quotaLeft: m }), // keep quotaLeft in sync
      setReputation: (r) => set({ reputation: r }),
      setLevel: (l) => set({ level: l }),
      setXP: (x) => set({ xp: x }),
      setMarketTicker: (arr) => set({ marketTicker: arr }),
      setChatLimits: (limits) => set({
        dailyQuota: limits.daily_quota || limits.dailyQuota || 0,
        quotaLeft: limits.quota_left ?? limits.quotaLeft ?? limits.remaining ?? 0,
        movesLeft: limits.quota_left ?? limits.quotaLeft ?? limits.remaining ?? 0,
        cooldownEndsAt: limits.cooldown_ends_at || limits.cooldownEndsAt || null,
      }),
      setLeaderboard: (lb) => set({ leaderboard: lb }),
      updateLeaderboard: (updateFn) =>
        set((state) => ({ leaderboard: updateFn(state.leaderboard) })),

      // Enhanced economic mutators
      setBoroughEconomy: (economy) => set({ boroughEconomy: economy }),
      updateBoroughEconomy: (boroughName, data) => 
        set((state) => ({
          boroughEconomy: {
            ...state.boroughEconomy,
            [boroughName]: { ...state.boroughEconomy[boroughName], ...data }
          }
        })),
      
      setMonthlyPot: (potData) => set({ monthlyPot: { ...potData } }),
      updateMonthlyPot: (updates) => 
        set((state) => ({
          monthlyPot: { ...state.monthlyPot, ...updates }
        })),
      
      addTransaction: (transaction) => 
        set((state) => ({
          transactions: [...state.transactions, { ...transaction, timestamp: Date.now() }]
        })),
      
      updateAgentPerformance: (agentName, performance) =>
        set((state) => ({
          agentPerformance: {
            ...state.agentPerformance,
            [agentName]: { ...state.agentPerformance[agentName], ...performance }
          }
        })),

      // FrameBridge turn-in-flight indicator (UI disable)
      waitingForDelta: false,
      setWaitingForDelta: (flag) => set({ waitingForDelta: !!flag }),

      // Generic delta merge helper used by frameBridge – shallow merge only
      applyDelta: (delta = {}) =>
        set((state) => ({
          ...delta,
          leaderboard: delta.leaderboard ?? state.leaderboard,
          playerCash: delta.playerCash ?? state.playerCash,
          potTotal: delta.potTotal ?? state.potTotal,
          quotaLeft: delta.quotaLeft ?? state.quotaLeft,
          movesLeft: delta.movesLeft ?? state.movesLeft,
          boroughEconomy: delta.boroughEconomy ?? state.boroughEconomy,
          monthlyPot: delta.monthlyPot ?? state.monthlyPot,
        })),
    }),
    {
      name: 'dcw_game_state', // localStorage key
      partialize: (state) => ({
        quotaLeft: state.quotaLeft,
        potTotal: state.potTotal,
        streak: state.streak,
        playerCash: state.playerCash,
        movesLeft: state.movesLeft,
        marketTicker: state.marketTicker,
        boroughs: state.boroughs,
        currentBorough: state.currentBorough,
        selectedCharacter: state.selectedCharacter,
        introCompleted: state.introCompleted,
        dailyQuota: state.dailyQuota,
        cooldownEndsAt: state.cooldownEndsAt,
        waitingForDelta: state.waitingForDelta,
        // Enhanced persistence
        boroughEconomy: state.boroughEconomy,
        monthlyPot: state.monthlyPot,
        agentPerformance: state.agentPerformance,
      }),
    },
  ),
);

export default useGameState; 